import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import type { SelectChangeEvent } from '@mui/material/Select';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import VerifiedIcon from '@mui/icons-material/Verified';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { useAuth } from '../auth/AuthContext';
import { useProjects } from '../context/ProjectContext';
import { DEFAULT_TITLE, usePortalBranding } from '../context/PortalContext';
import { resolveApiAssetUrl } from '../api/client';
import { notify } from '../components/Notifier';
import { topBarLogoHeight } from '../components/portalLogoSize';
import { displayIdentity } from '../utils/userIdentity';
import { ChangePasswordDialog } from './ChangePasswordDialog';

interface TopBarProps {
  onToggleSidebar: () => void;
}

/**
 * Chữ cái đại diện trên avatar: lấy chữ đầu của TỪ CUỐI, đúng cách gọi tên
 * tiếng Việt ("Nguyễn Văn An" → "A").
 *
 * Phải kiểm tra chuỗi rỗng TRƯỚC khi split: `''.split(/\s+/)` trả `['']` chứ
 * không phải `[]`, nên điều kiện `parts.length === 0` không bao giờ đúng và
 * tên rỗng sẽ cho ra avatar trống trơn thay vì dấu "?".
 */
/**
 * Màn hình nơi ô chọn dự án trên TopBar không có tác dụng gì (không lọc,
 * không điều hướng) — selectedProjectId không được các trang này đọc tới.
 * Ẩn đi để đỡ gây hiểu nhầm là chọn được gì đó ở đây.
 */
function isProjectSelectorHidden(pathname: string): boolean {
  return pathname.startsWith('/admin') || pathname.startsWith('/feature-requests');
}

function initials(name: string): string {
  const trimmed = name.trim();
  if (trimmed.length === 0) return '?';
  const parts = trimmed.split(/\s+/);
  const last = parts[parts.length - 1];
  return last.charAt(0).toUpperCase() || '?';
}

export function TopBar({ onToggleSidebar }: TopBarProps): JSX.Element {
  const { user, logout } = useAuth();
  const { projects, selectedProjectId, setSelectedProjectId } = useProjects();
  const { branding } = usePortalBranding();
  const logoSrc = resolveApiAssetUrl(branding?.logo_url);
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  const handleLogout = async () => {
    setAnchorEl(null);
    await logout();
    notify('Đã đăng xuất', 'success');
    navigate('/login', { replace: true });
  };

  /**
   * Đang xem chi tiết một dự án thì đổi ô chọn = chuyển sang dự án đó. Các
   * trang khác (bảng điều khiển, bug, yêu cầu thay đổi) chỉ lọc theo lựa chọn
   * nên ở nguyên chỗ.
   *
   * `'/projects'.startsWith('/projects/')` là false, nên trang DANH SÁCH tự
   * nhiên nằm ngoài — ở đó đổi ô chọn chỉ ghi nhận lựa chọn, không kéo người
   * dùng ra khỏi danh sách họ đang xem.
   *
   * Điều hướng bỏ luôn query ?phaseId=&featureId= của dự án cũ: giai đoạn và
   * tính năng đó không thuộc dự án mới.
   */
  const handleProjectChange = (e: SelectChangeEvent) => {
    const nextId = e.target.value || null;
    setSelectedProjectId(nextId);
    if (nextId && location.pathname.startsWith('/projects/')) {
      navigate(`/projects/${nextId}`);
    }
  };

  return (
    <AppBar
      position="fixed"
      color="default"
      sx={{
        zIndex: (t) => t.zIndex.drawer + 1,
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
      }}
    >
      <Toolbar>
        <IconButton
          color="inherit"
          edge="start"
          onClick={onToggleSidebar}
          aria-label="Mở/đóng thanh điều hướng"
          sx={{ mr: 2 }}
        >
          <MenuIcon />
        </IconButton>

        {logoSrc ? (
          <Box
            component="img"
            src={logoSrc}
            alt={branding?.display_name ?? ''}
            sx={{ height: topBarLogoHeight(branding?.logo_height), mr: 1 }}
          />
        ) : (
          <VerifiedIcon sx={{ mr: 1 }} />
        )}
        <Typography
          variant="h6"
          noWrap
          sx={{ fontWeight: 700, display: { xs: 'none', sm: 'block' } }}
        >
          {branding?.display_name ?? DEFAULT_TITLE}
        </Typography>

        <Box sx={{ flexGrow: 1 }} />

        {projects.length > 0 && !isProjectSelectorHidden(location.pathname) && (
          <FormControl size="small" sx={{ minWidth: { xs: 140, sm: 220 }, mr: 2 }}>
            <Select
              value={selectedProjectId ?? ''}
              onChange={handleProjectChange}
              displayEmpty
              aria-label="Chọn dự án"
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                color: 'inherit',
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                '& .MuiSvgIcon-root': { color: 'inherit' },
              }}
            >
              {projects.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <IconButton
          onClick={(e) => setAnchorEl(e.currentTarget)}
          size="small"
          aria-label="Tài khoản"
          aria-haspopup="true"
        >
          <Avatar sx={{ width: 36, height: 36, bgcolor: 'secondary.main' }}>
            {user ? initials(user.name) : '?'}
          </Avatar>
        </IconButton>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem disabled sx={{ opacity: '1 !important' }}>
            <Box>
              <Typography variant="subtitle2">{user?.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {user && displayIdentity(user)}
              </Typography>
            </Box>
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              setChangePasswordOpen(true);
            }}
          >
            <ListItemIcon>
              <VpnKeyIcon fontSize="small" />
            </ListItemIcon>
            {user?.has_password ? 'Đổi mật khẩu' : 'Đặt mật khẩu'}
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            Đăng xuất
          </MenuItem>
        </Menu>
      </Toolbar>

      <ChangePasswordDialog open={changePasswordOpen} onClose={() => setChangePasswordOpen(false)} />
    </AppBar>
  );
}
